import {DataTable} from '@management-ui/core';
import {MTableToolbar} from '@material-table/core';
import {reverse} from 'named-urls';
import * as React from 'react';
import {useContext, useEffect, useMemo, useRef} from 'react';
import {useHistory} from 'react-router-dom';
import ConfirmedDate from '../../../../components/ConfirmedDate';
import Filter from '../../../../components/Filter';
import OrderStatus from '../../../../components/OrderStatus';
import {ServiceContext} from '../../../../components/Services';
import Value from '../../../../components/Value';
import {useOrderStatuses, useQuery} from '../../../../hooks';
import routes from '../../../../routes';
import {API} from '../../../../services/API';
import {firstLine} from '../../../../utilities';

const Table = ({archive = false}) => {
  const services = useContext(ServiceContext);
  const history = useHistory();
  const statuses = useOrderStatuses();

  /** @type {({current: DataTable})} */
  const tableRef = useRef();
  const refreshRef = useRef('');
  const {values: {status, page, pageSize, search}, changeQuery, changePage, initialised} = useQuery(useMemo(() => ({
    status: 'ongoing',
    page: '1',
    pageSize: '50',
    search: ''
  }), []));

  useEffect(() => {
    const key = JSON.stringify({status});
    if (initialised && refreshRef.current !== key && tableRef.current) {
      tableRef.current.refresh();
    }
    refreshRef.current = key;
  }, [status, initialised]);

  const tableProps = useMemo(() => ({
    tableProps: {
      components: {
        Toolbar: (props) => (
          <>
            <MTableToolbar {...props}/>
            <Filter
              statuses={statuses}
              status={status}
              onChangeStatus={(changed) => changeQuery({status: changed})}
            />
          </>
        )
      }
    },
    options: {
      initialPage: parseInt(page) - 1,
      pageSize: parseInt(pageSize),
      searchText: search
    },
    columns: [
      {title: 'PO Number', field: 'reference'},
      {
        title: 'Loading Date',
        field: 'collection_date',
        render: item => <ConfirmedDate date={item.collection_date} confirmed={item.collection_date_confirmed}/>
      },
      {title: 'Supplier', field: 'supplier', render: item => item.supplier?.name ?? '-'},
      {title: 'Depot', field: 'depot', render: item => firstLine(item.delivery_address)},
      {
        title: 'Value',
        field: 'value',
        render: item => <Value value={item.value} currency={item.currency} finalised={!!item.weighed_at}/>
      },
      {
        title: 'Status',
        sorting: false,
        field: 'locked',
        render: item => <OrderStatus order={item}/>
      },
    ],
    loadData: query => new Promise((resolve, reject) => {
      const params = {};
      if (archive) {
        params.archive = 'only';
      }
      if (status !== 'all') {
        params.status = status;
      }
      changePage(API.getTableParams(query));
      services.purchaseOrder.getTableOrders(query, params)
        .then(response => {
          resolve({
            data: response.data, page: response.meta.current_page - 1, totalCount: response.meta.total
          });
        }).catch(() => reject());
    }),
    onRowClick: (order) => history.push(reverse(order.archived ? routes.archive.purchaseOrder.detail : routes.purchaseOrders.detail, {id: order.id}))
  }), [archive, services, history, statuses, status, page, pageSize, search, changeQuery, changePage])

  return initialised ? (
    <DataTable
      ref={tableRef}
      title=""
      {...tableProps}
    />
  ) : null;
};

export default Table;
